/**
 * Book-level themes, motifs, and symbols roster (desktop-v2).
 * Source of truth: `book.metadata.themes` / `motifs` / `symbols`.
 * Chapter `tabData.themesMotifs` is a derived name list.
 */

export type ThemeType = "major" | "minor";

export type ThemeChapterAppearance = {
  chapterId: string;
  /** How the theme manifests in this chapter. */
  manifestation?: string;
};

export type MotifChapterAppearance = {
  chapterId: string;
  /** Context / how the motif appears in this chapter. */
  context?: string;
};

export type SymbolChapterAppearance = {
  chapterId: string;
  /** Context / how the symbol appears in this chapter. */
  context?: string;
};

export type BookTheme = {
  id: string;
  name: string;
  type: ThemeType;
  description: string;
  /** How the theme develops across chapters. */
  evolutionNotes: string;
  firstAppearanceChapterId?: string;
  appearances: ThemeChapterAppearance[];
  updatedAt: string;
};

export type BookMotif = {
  id: string;
  name: string;
  description: string;
  firstAppearanceChapterId?: string;
  appearances: MotifChapterAppearance[];
  updatedAt: string;
};

export type BookSymbol = {
  id: string;
  name: string;
  meaning: string;
  firstAppearanceChapterId?: string;
  appearances: SymbolChapterAppearance[];
  updatedAt: string;
};

export type ThemeKind = "theme" | "motif" | "symbol";

/** Author-removed theme with reason — informs future extract / roster versions. */
export type ThemeRemovalRecord = {
  id: string;
  themeId?: string;
  name: string;
  type?: ThemeType;
  description?: string;
  reason: string;
  removedAt: string;
};

export type MotifRemovalRecord = {
  id: string;
  motifId?: string;
  name: string;
  description?: string;
  reason: string;
  removedAt: string;
};

export type SymbolRemovalRecord = {
  id: string;
  symbolId?: string;
  name: string;
  meaning?: string;
  reason: string;
  removedAt: string;
};

export function generateThemeId(): string {
  return `theme-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function generateMotifId(): string {
  return `motif-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function generateSymbolId(): string {
  return `symbol-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function generateThemeRemovalId(): string {
  return `theme-removal-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function generateMotifRemovalId(): string {
  return `motif-removal-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function generateSymbolRemovalId(): string {
  return `symbol-removal-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function normalizeNameKey(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, " ");
}

/** Reject empty / trivial scrapes from LLM or tab lines. */
export function isPlausibleThemeName(name: string): boolean {
  const trimmed = name.trim();
  if (!trimmed || trimmed.length < 2) return false;
  const key = normalizeNameKey(trimmed);
  if (!key) return false;
  if (!/[a-zA-Z]/.test(trimmed)) return false;
  // Single articles / placeholders
  if (key === "the" || key === "a" || key === "an" || key === "theme" || key === "motif" || key === "symbol") {
    return false;
  }
  return true;
}

export function filterPlausibleExtractedThemes<T extends { name?: string }>(items: T[]): T[] {
  return items.filter((item) => {
    const name = typeof item.name === "string" ? item.name.trim() : "";
    return name && isPlausibleThemeName(name);
  });
}

export function normalizeThemeType(value: unknown): ThemeType {
  if (typeof value !== "string") return "minor";
  const key = value.trim().toLowerCase();
  return key === "major" ? "major" : "minor";
}

export function normalizeThemeAppearance(raw: unknown): ThemeChapterAppearance | null {
  if (!raw || typeof raw !== "object") return null;
  const rec = raw as Record<string, unknown>;
  const chapterId =
    typeof rec.chapterId === "string"
      ? rec.chapterId.trim()
      : typeof rec.chapter_id === "string"
        ? rec.chapter_id.trim()
        : "";
  if (!chapterId) return null;
  const manifestation =
    typeof rec.manifestation === "string"
      ? rec.manifestation.trim()
      : typeof rec.context === "string"
        ? rec.context.trim()
        : typeof rec.note === "string"
          ? rec.note.trim()
          : undefined;
  return {
    chapterId,
    ...(manifestation ? { manifestation } : {}),
  };
}

export function normalizeMotifAppearance(raw: unknown): MotifChapterAppearance | null {
  if (!raw || typeof raw !== "object") return null;
  const rec = raw as Record<string, unknown>;
  const chapterId =
    typeof rec.chapterId === "string"
      ? rec.chapterId.trim()
      : typeof rec.chapter_id === "string"
        ? rec.chapter_id.trim()
        : "";
  if (!chapterId) return null;
  const context =
    typeof rec.context === "string"
      ? rec.context.trim()
      : typeof rec.note === "string"
        ? rec.note.trim()
        : typeof rec.manifestation === "string"
          ? rec.manifestation.trim()
          : undefined;
  return {
    chapterId,
    ...(context ? { context } : {}),
  };
}

export function normalizeSymbolAppearance(raw: unknown): SymbolChapterAppearance | null {
  return normalizeMotifAppearance(raw);
}

function appearancesFromLegacyChapterAppearances(
  raw: unknown,
  kind: "theme" | "motif" | "symbol"
): ThemeChapterAppearance[] | MotifChapterAppearance[] | SymbolChapterAppearance[] {
  if (!Array.isArray(raw)) return [];
  const out: Array<ThemeChapterAppearance | MotifChapterAppearance | SymbolChapterAppearance> = [];
  for (const item of raw) {
    if (kind === "theme") {
      const appearance = normalizeThemeAppearance(item);
      if (appearance) out.push(appearance);
    } else {
      const appearance = normalizeMotifAppearance(item);
      if (appearance) out.push(appearance);
    }
  }
  return out;
}

export function normalizeBookTheme(raw: unknown, nowIso = new Date().toISOString()): BookTheme | null {
  if (!raw || typeof raw !== "object") return null;
  const rec = raw as Record<string, unknown>;
  const name = typeof rec.name === "string" ? rec.name.trim() : "";
  if (!name) return null;
  const id = typeof rec.id === "string" && rec.id.trim() ? rec.id.trim() : generateThemeId();
  const type = normalizeThemeType(rec.type);
  const description = typeof rec.description === "string" ? rec.description.trim() : "";
  const evolutionNotes =
    typeof rec.evolutionNotes === "string"
      ? rec.evolutionNotes.trim()
      : typeof rec.evolution_notes === "string"
        ? rec.evolution_notes.trim()
        : "";
  const firstAppearanceChapterId =
    typeof rec.firstAppearanceChapterId === "string" && rec.firstAppearanceChapterId.trim()
      ? rec.firstAppearanceChapterId.trim()
      : undefined;

  let appearances: ThemeChapterAppearance[] = [];
  if (Array.isArray(rec.appearances)) {
    appearances = appearancesFromLegacyChapterAppearances(rec.appearances, "theme") as ThemeChapterAppearance[];
  } else if (Array.isArray(rec.chapterAppearances)) {
    // Legacy Theme shape
    appearances = appearancesFromLegacyChapterAppearances(
      rec.chapterAppearances,
      "theme"
    ) as ThemeChapterAppearance[];
  }

  const updatedAt =
    typeof rec.updatedAt === "string" && rec.updatedAt.trim() ? rec.updatedAt.trim() : nowIso;

  const theme: BookTheme = {
    id,
    name,
    type,
    description,
    evolutionNotes,
    appearances,
    updatedAt,
  };
  if (firstAppearanceChapterId) theme.firstAppearanceChapterId = firstAppearanceChapterId;
  else if (appearances[0]?.chapterId) theme.firstAppearanceChapterId = appearances[0].chapterId;
  return theme;
}

export function normalizeBookMotif(raw: unknown, nowIso = new Date().toISOString()): BookMotif | null {
  if (!raw || typeof raw !== "object") return null;
  const rec = raw as Record<string, unknown>;
  const name = typeof rec.name === "string" ? rec.name.trim() : "";
  if (!name) return null;
  const id = typeof rec.id === "string" && rec.id.trim() ? rec.id.trim() : generateMotifId();
  const description = typeof rec.description === "string" ? rec.description.trim() : "";
  const firstAppearanceChapterId =
    typeof rec.firstAppearanceChapterId === "string" && rec.firstAppearanceChapterId.trim()
      ? rec.firstAppearanceChapterId.trim()
      : undefined;

  let appearances: MotifChapterAppearance[] = [];
  if (Array.isArray(rec.appearances)) {
    appearances = appearancesFromLegacyChapterAppearances(rec.appearances, "motif") as MotifChapterAppearance[];
  } else if (Array.isArray(rec.chapterAppearances)) {
    appearances = appearancesFromLegacyChapterAppearances(
      rec.chapterAppearances,
      "motif"
    ) as MotifChapterAppearance[];
  }

  const updatedAt =
    typeof rec.updatedAt === "string" && rec.updatedAt.trim() ? rec.updatedAt.trim() : nowIso;

  const motif: BookMotif = {
    id,
    name,
    description,
    appearances,
    updatedAt,
  };
  if (firstAppearanceChapterId) motif.firstAppearanceChapterId = firstAppearanceChapterId;
  else if (appearances[0]?.chapterId) motif.firstAppearanceChapterId = appearances[0].chapterId;
  return motif;
}

export function normalizeBookSymbol(raw: unknown, nowIso = new Date().toISOString()): BookSymbol | null {
  if (!raw || typeof raw !== "object") return null;
  const rec = raw as Record<string, unknown>;
  const name = typeof rec.name === "string" ? rec.name.trim() : "";
  if (!name) return null;
  const id = typeof rec.id === "string" && rec.id.trim() ? rec.id.trim() : generateSymbolId();
  const meaning =
    typeof rec.meaning === "string"
      ? rec.meaning.trim()
      : typeof rec.description === "string"
        ? rec.description.trim()
        : "";
  const firstAppearanceChapterId =
    typeof rec.firstAppearanceChapterId === "string" && rec.firstAppearanceChapterId.trim()
      ? rec.firstAppearanceChapterId.trim()
      : undefined;

  let appearances: SymbolChapterAppearance[] = [];
  if (Array.isArray(rec.appearances)) {
    appearances = appearancesFromLegacyChapterAppearances(rec.appearances, "symbol") as SymbolChapterAppearance[];
  } else if (Array.isArray(rec.chapterAppearances)) {
    appearances = appearancesFromLegacyChapterAppearances(
      rec.chapterAppearances,
      "symbol"
    ) as SymbolChapterAppearance[];
  }

  const updatedAt =
    typeof rec.updatedAt === "string" && rec.updatedAt.trim() ? rec.updatedAt.trim() : nowIso;

  const symbol: BookSymbol = {
    id,
    name,
    meaning,
    appearances,
    updatedAt,
  };
  if (firstAppearanceChapterId) symbol.firstAppearanceChapterId = firstAppearanceChapterId;
  else if (appearances[0]?.chapterId) symbol.firstAppearanceChapterId = appearances[0].chapterId;
  return symbol;
}

export function sortThemesMajorFirst(themes: BookTheme[]): BookTheme[] {
  return [...themes].sort((a, b) => {
    if (a.type !== b.type) return a.type === "major" ? -1 : 1;
    return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
  });
}

export function sortByName<T extends { name: string }>(items: T[]): T[] {
  return [...items].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
}

export function normalizeThemeRoster(raw: unknown): BookTheme[] {
  if (!Array.isArray(raw)) return [];
  const out: BookTheme[] = [];
  const seenIds = new Set<string>();
  for (const item of raw) {
    const theme = normalizeBookTheme(item);
    if (!theme) continue;
    if (!isPlausibleThemeName(theme.name)) continue;
    if (seenIds.has(theme.id)) {
      theme.id = generateThemeId();
    }
    seenIds.add(theme.id);
    out.push(theme);
  }
  return sortThemesMajorFirst(out);
}

export function normalizeMotifRoster(raw: unknown): BookMotif[] {
  if (!Array.isArray(raw)) return [];
  const out: BookMotif[] = [];
  const seenIds = new Set<string>();
  for (const item of raw) {
    const motif = normalizeBookMotif(item);
    if (!motif) continue;
    if (!isPlausibleThemeName(motif.name)) continue;
    if (seenIds.has(motif.id)) {
      motif.id = generateMotifId();
    }
    seenIds.add(motif.id);
    out.push(motif);
  }
  return sortByName(out);
}

export function normalizeSymbolRoster(raw: unknown): BookSymbol[] {
  if (!Array.isArray(raw)) return [];
  const out: BookSymbol[] = [];
  const seenIds = new Set<string>();
  for (const item of raw) {
    const symbol = normalizeBookSymbol(item);
    if (!symbol) continue;
    if (!isPlausibleThemeName(symbol.name)) continue;
    if (seenIds.has(symbol.id)) {
      symbol.id = generateSymbolId();
    }
    seenIds.add(symbol.id);
    out.push(symbol);
  }
  return sortByName(out);
}

export function findByName<T extends { name: string }>(roster: T[], name: string): T | undefined {
  const key = normalizeNameKey(name);
  if (!key) return undefined;
  const exact = roster.find((item) => normalizeNameKey(item.name) === key);
  if (exact) return exact;
  // Soft match: shorter name contained as whole token
  return roster.find((item) => {
    const itemKey = normalizeNameKey(item.name);
    const itemTokens = new Set(itemKey.split(" ").filter((t) => t.length > 1));
    const tokens = key.split(" ").filter((t) => t.length > 1);
    if (tokens.length === 1 && itemTokens.has(tokens[0]!)) return true;
    if (tokens.length > 1 && tokens.every((t) => itemTokens.has(t))) return true;
    return false;
  });
}

export type ExtractedThemePayload = {
  name: string;
  type?: ThemeType | string;
  description?: string;
  evolutionNotes?: string;
  manifestation?: string;
  chapterIds?: string[];
};

export type ExtractedMotifPayload = {
  name: string;
  description?: string;
  context?: string;
  chapterIds?: string[];
};

export type ExtractedSymbolPayload = {
  name: string;
  meaning?: string;
  context?: string;
  chapterIds?: string[];
};

export function mergeExtractedThemesIntoRoster(params: {
  roster: BookTheme[];
  extracted: ExtractedThemePayload[];
  chapterId: string;
  removals?: ThemeRemovalRecord[];
  nowIso?: string;
}): BookTheme[] {
  const now = params.nowIso ?? new Date().toISOString();
  const removals = params.removals ?? [];
  let next = [...params.roster];

  for (const item of params.extracted) {
    const name = typeof item.name === "string" ? item.name.trim() : "";
    if (!name || !isPlausibleThemeName(name)) continue;
    if (isThemeNameRemoved(removals, name)) continue;
    const type = normalizeThemeType(item.type);
    const description = typeof item.description === "string" ? item.description.trim() : "";
    const evolutionNotes =
      typeof item.evolutionNotes === "string" ? item.evolutionNotes.trim() : "";
    const manifestation =
      typeof item.manifestation === "string" ? item.manifestation.trim() : undefined;

    const existing = findByName(next, name);
    if (existing) {
      const preferName = name.length > existing.name.length ? name : existing.name;
      const nextType: ThemeType =
        existing.type === "major" || type === "major" ? "major" : "minor";
      const appearance: ThemeChapterAppearance = {
        chapterId: params.chapterId,
        ...(manifestation ? { manifestation } : {}),
      };
      const otherAppearances = existing.appearances.filter((a) => a.chapterId !== params.chapterId);
      const prevAppearance = existing.appearances.find((a) => a.chapterId === params.chapterId);
      if (prevAppearance?.manifestation && !appearance.manifestation) {
        appearance.manifestation = prevAppearance.manifestation;
      }

      next = next.map((t) =>
        t.id === existing.id
          ? {
              ...t,
              name: preferName,
              type: nextType,
              description: t.description.trim() || description,
              evolutionNotes: t.evolutionNotes.trim() || evolutionNotes,
              firstAppearanceChapterId: t.firstAppearanceChapterId ?? params.chapterId,
              appearances: [...otherAppearances, appearance],
              updatedAt: now,
            }
          : t
      );
    } else {
      next.push({
        id: generateThemeId(),
        name,
        type,
        description,
        evolutionNotes,
        firstAppearanceChapterId: params.chapterId,
        appearances: [
          {
            chapterId: params.chapterId,
            ...(manifestation ? { manifestation } : {}),
          },
        ],
        updatedAt: now,
      });
    }
  }

  return sortThemesMajorFirst(next);
}

export function mergeExtractedMotifsIntoRoster(params: {
  roster: BookMotif[];
  extracted: ExtractedMotifPayload[];
  chapterId: string;
  removals?: MotifRemovalRecord[];
  nowIso?: string;
}): BookMotif[] {
  const now = params.nowIso ?? new Date().toISOString();
  const removals = params.removals ?? [];
  let next = [...params.roster];

  for (const item of params.extracted) {
    const name = typeof item.name === "string" ? item.name.trim() : "";
    if (!name || !isPlausibleThemeName(name)) continue;
    if (isMotifNameRemoved(removals, name)) continue;
    const description = typeof item.description === "string" ? item.description.trim() : "";
    const context = typeof item.context === "string" ? item.context.trim() : undefined;

    const existing = findByName(next, name);
    if (existing) {
      const preferName = name.length > existing.name.length ? name : existing.name;
      const appearance: MotifChapterAppearance = {
        chapterId: params.chapterId,
        ...(context ? { context } : {}),
      };
      const otherAppearances = existing.appearances.filter((a) => a.chapterId !== params.chapterId);
      const prevAppearance = existing.appearances.find((a) => a.chapterId === params.chapterId);
      if (prevAppearance?.context && !appearance.context) {
        appearance.context = prevAppearance.context;
      }

      next = next.map((m) =>
        m.id === existing.id
          ? {
              ...m,
              name: preferName,
              description: m.description.trim() || description,
              firstAppearanceChapterId: m.firstAppearanceChapterId ?? params.chapterId,
              appearances: [...otherAppearances, appearance],
              updatedAt: now,
            }
          : m
      );
    } else {
      next.push({
        id: generateMotifId(),
        name,
        description,
        firstAppearanceChapterId: params.chapterId,
        appearances: [
          {
            chapterId: params.chapterId,
            ...(context ? { context } : {}),
          },
        ],
        updatedAt: now,
      });
    }
  }

  return sortByName(next);
}

export function mergeExtractedSymbolsIntoRoster(params: {
  roster: BookSymbol[];
  extracted: ExtractedSymbolPayload[];
  chapterId: string;
  removals?: SymbolRemovalRecord[];
  nowIso?: string;
}): BookSymbol[] {
  const now = params.nowIso ?? new Date().toISOString();
  const removals = params.removals ?? [];
  let next = [...params.roster];

  for (const item of params.extracted) {
    const name = typeof item.name === "string" ? item.name.trim() : "";
    if (!name || !isPlausibleThemeName(name)) continue;
    if (isSymbolNameRemoved(removals, name)) continue;
    const meaning = typeof item.meaning === "string" ? item.meaning.trim() : "";
    const context = typeof item.context === "string" ? item.context.trim() : undefined;

    const existing = findByName(next, name);
    if (existing) {
      const preferName = name.length > existing.name.length ? name : existing.name;
      const appearance: SymbolChapterAppearance = {
        chapterId: params.chapterId,
        ...(context ? { context } : {}),
      };
      const otherAppearances = existing.appearances.filter((a) => a.chapterId !== params.chapterId);
      const prevAppearance = existing.appearances.find((a) => a.chapterId === params.chapterId);
      if (prevAppearance?.context && !appearance.context) {
        appearance.context = prevAppearance.context;
      }

      next = next.map((s) =>
        s.id === existing.id
          ? {
              ...s,
              name: preferName,
              meaning: s.meaning.trim() || meaning,
              firstAppearanceChapterId: s.firstAppearanceChapterId ?? params.chapterId,
              appearances: [...otherAppearances, appearance],
              updatedAt: now,
            }
          : s
      );
    } else {
      next.push({
        id: generateSymbolId(),
        name,
        meaning,
        firstAppearanceChapterId: params.chapterId,
        appearances: [
          {
            chapterId: params.chapterId,
            ...(context ? { context } : {}),
          },
        ],
        updatedAt: now,
      });
    }
  }

  return sortByName(next);
}

/**
 * Merge a whole-book themes pass. Holistic description/evolution are authoritative.
 * Existing chapter manifestations are preserved.
 */
export function mergeHolisticThemesIntoRoster(params: {
  roster: BookTheme[];
  extracted: ExtractedThemePayload[];
  validChapterIds: string[];
  removals?: ThemeRemovalRecord[];
  nowIso?: string;
}): BookTheme[] {
  const now = params.nowIso ?? new Date().toISOString();
  const validIds = new Set(params.validChapterIds);
  const removals = params.removals ?? [];
  let next = [...params.roster];

  for (const item of params.extracted) {
    const name = typeof item.name === "string" ? item.name.trim() : "";
    if (!name || !isPlausibleThemeName(name) || isThemeNameRemoved(removals, name)) continue;

    const type = normalizeThemeType(item.type);
    const description = typeof item.description === "string" ? item.description.trim() : "";
    const evolutionNotes =
      typeof item.evolutionNotes === "string" ? item.evolutionNotes.trim() : "";
    const chapterIds = Array.from(
      new Set(
        (Array.isArray(item.chapterIds) ? item.chapterIds : [])
          .filter((id): id is string => typeof id === "string")
          .map((id) => id.trim())
          .filter((id) => validIds.has(id))
      )
    );
    const existing = findByName(next, name);

    if (existing) {
      const existingByChapter = new Map(
        existing.appearances.map((appearance) => [appearance.chapterId, appearance])
      );
      const mergedChapterIds = Array.from(
        new Set([...existing.appearances.map((a) => a.chapterId), ...chapterIds])
      ).filter((id) => validIds.has(id));
      const appearances = mergedChapterIds.map(
        (chapterId) => existingByChapter.get(chapterId) ?? { chapterId }
      );
      const preferName = name.length > existing.name.length ? name : existing.name;
      next = next.map((theme) =>
        theme.id === existing.id
          ? {
              ...theme,
              name: preferName,
              type,
              description: description || theme.description,
              evolutionNotes: evolutionNotes || theme.evolutionNotes,
              firstAppearanceChapterId: theme.firstAppearanceChapterId ?? chapterIds[0],
              appearances,
              updatedAt: now,
            }
          : theme
      );
      continue;
    }

    next.push({
      id: generateThemeId(),
      name,
      type,
      description,
      evolutionNotes,
      firstAppearanceChapterId: chapterIds[0],
      appearances: chapterIds.map((chapterId) => ({ chapterId })),
      updatedAt: now,
    });
  }

  return sortThemesMajorFirst(next);
}

export function mergeHolisticMotifsIntoRoster(params: {
  roster: BookMotif[];
  extracted: ExtractedMotifPayload[];
  validChapterIds: string[];
  removals?: MotifRemovalRecord[];
  nowIso?: string;
}): BookMotif[] {
  const now = params.nowIso ?? new Date().toISOString();
  const validIds = new Set(params.validChapterIds);
  const removals = params.removals ?? [];
  let next = [...params.roster];

  for (const item of params.extracted) {
    const name = typeof item.name === "string" ? item.name.trim() : "";
    if (!name || !isPlausibleThemeName(name) || isMotifNameRemoved(removals, name)) continue;

    const description = typeof item.description === "string" ? item.description.trim() : "";
    const chapterIds = Array.from(
      new Set(
        (Array.isArray(item.chapterIds) ? item.chapterIds : [])
          .filter((id): id is string => typeof id === "string")
          .map((id) => id.trim())
          .filter((id) => validIds.has(id))
      )
    );
    const existing = findByName(next, name);

    if (existing) {
      const existingByChapter = new Map(
        existing.appearances.map((appearance) => [appearance.chapterId, appearance])
      );
      const mergedChapterIds = Array.from(
        new Set([...existing.appearances.map((a) => a.chapterId), ...chapterIds])
      ).filter((id) => validIds.has(id));
      const appearances = mergedChapterIds.map(
        (chapterId) => existingByChapter.get(chapterId) ?? { chapterId }
      );
      const preferName = name.length > existing.name.length ? name : existing.name;
      next = next.map((motif) =>
        motif.id === existing.id
          ? {
              ...motif,
              name: preferName,
              description: description || motif.description,
              firstAppearanceChapterId: motif.firstAppearanceChapterId ?? chapterIds[0],
              appearances,
              updatedAt: now,
            }
          : motif
      );
      continue;
    }

    next.push({
      id: generateMotifId(),
      name,
      description,
      firstAppearanceChapterId: chapterIds[0],
      appearances: chapterIds.map((chapterId) => ({ chapterId })),
      updatedAt: now,
    });
  }

  return sortByName(next);
}

export function mergeHolisticSymbolsIntoRoster(params: {
  roster: BookSymbol[];
  extracted: ExtractedSymbolPayload[];
  validChapterIds: string[];
  removals?: SymbolRemovalRecord[];
  nowIso?: string;
}): BookSymbol[] {
  const now = params.nowIso ?? new Date().toISOString();
  const validIds = new Set(params.validChapterIds);
  const removals = params.removals ?? [];
  let next = [...params.roster];

  for (const item of params.extracted) {
    const name = typeof item.name === "string" ? item.name.trim() : "";
    if (!name || !isPlausibleThemeName(name) || isSymbolNameRemoved(removals, name)) continue;

    const meaning = typeof item.meaning === "string" ? item.meaning.trim() : "";
    const chapterIds = Array.from(
      new Set(
        (Array.isArray(item.chapterIds) ? item.chapterIds : [])
          .filter((id): id is string => typeof id === "string")
          .map((id) => id.trim())
          .filter((id) => validIds.has(id))
      )
    );
    const existing = findByName(next, name);

    if (existing) {
      const existingByChapter = new Map(
        existing.appearances.map((appearance) => [appearance.chapterId, appearance])
      );
      const mergedChapterIds = Array.from(
        new Set([...existing.appearances.map((a) => a.chapterId), ...chapterIds])
      ).filter((id) => validIds.has(id));
      const appearances = mergedChapterIds.map(
        (chapterId) => existingByChapter.get(chapterId) ?? { chapterId }
      );
      const preferName = name.length > existing.name.length ? name : existing.name;
      next = next.map((symbol) =>
        symbol.id === existing.id
          ? {
              ...symbol,
              name: preferName,
              meaning: meaning || symbol.meaning,
              firstAppearanceChapterId: symbol.firstAppearanceChapterId ?? chapterIds[0],
              appearances,
              updatedAt: now,
            }
          : symbol
      );
      continue;
    }

    next.push({
      id: generateSymbolId(),
      name,
      meaning,
      firstAppearanceChapterId: chapterIds[0],
      appearances: chapterIds.map((chapterId) => ({ chapterId })),
      updatedAt: now,
    });
  }

  return sortByName(next);
}

/** Derive newline name list for a chapter's Themes & Motifs tab. */
export function chapterThemesMotifsTabText(
  themes: BookTheme[],
  motifs: BookMotif[],
  symbols: BookSymbol[],
  chapterId: string
): string {
  const themeNames = sortThemesMajorFirst(
    themes.filter((t) => t.appearances.some((a) => a.chapterId === chapterId))
  ).map((t) => t.name);
  const motifNames = sortByName(
    motifs.filter((m) => m.appearances.some((a) => a.chapterId === chapterId))
  ).map((m) => m.name);
  const symbolNames = sortByName(
    symbols.filter((s) => s.appearances.some((a) => a.chapterId === chapterId))
  ).map((s) => s.name);
  return [...themeNames, ...motifNames, ...symbolNames].join("\n");
}

export function syncAllChapterThemesMotifsTabs(
  chapters: Array<{ id: string; tabData: { themesMotifs: string } }>,
  themes: BookTheme[],
  motifs: BookMotif[],
  symbols: BookSymbol[]
): void {
  for (const chapter of chapters) {
    chapter.tabData.themesMotifs = chapterThemesMotifsTabText(themes, motifs, symbols, chapter.id);
  }
}

/** Flat name list for Story Craft / other consumers that still want a string. */
export function themesMotifsRosterAsString(
  themes: BookTheme[],
  motifs: BookMotif[],
  symbols: BookSymbol[]
): string {
  const names = [
    ...sortThemesMajorFirst(themes).map((t) => t.name),
    ...sortByName(motifs).map((m) => m.name),
    ...sortByName(symbols).map((s) => s.name),
  ];
  return names.join("\n");
}

export function getThemesFromBookMetadata(
  metadata: Record<string, unknown> | undefined | null
): BookTheme[] {
  if (!metadata) return [];
  return normalizeThemeRoster(metadata.themes);
}

export function getMotifsFromBookMetadata(
  metadata: Record<string, unknown> | undefined | null
): BookMotif[] {
  if (!metadata) return [];
  return normalizeMotifRoster(metadata.motifs);
}

export function getSymbolsFromBookMetadata(
  metadata: Record<string, unknown> | undefined | null
): BookSymbol[] {
  if (!metadata) return [];
  return normalizeSymbolRoster(metadata.symbols);
}

export function normalizeThemeRemoval(raw: unknown): ThemeRemovalRecord | null {
  if (!raw || typeof raw !== "object") return null;
  const rec = raw as Record<string, unknown>;
  const name = typeof rec.name === "string" ? rec.name.trim() : "";
  const reason = typeof rec.reason === "string" ? rec.reason.trim() : "";
  if (!name || !reason) return null;
  const id =
    typeof rec.id === "string" && rec.id.trim() ? rec.id.trim() : generateThemeRemovalId();
  return {
    id,
    themeId: typeof rec.themeId === "string" ? rec.themeId.trim() : undefined,
    name,
    type: rec.type != null ? normalizeThemeType(rec.type) : undefined,
    description: typeof rec.description === "string" ? rec.description.trim() : undefined,
    reason,
    removedAt:
      typeof rec.removedAt === "string" && rec.removedAt.trim()
        ? rec.removedAt.trim()
        : new Date().toISOString(),
  };
}

export function normalizeMotifRemoval(raw: unknown): MotifRemovalRecord | null {
  if (!raw || typeof raw !== "object") return null;
  const rec = raw as Record<string, unknown>;
  const name = typeof rec.name === "string" ? rec.name.trim() : "";
  const reason = typeof rec.reason === "string" ? rec.reason.trim() : "";
  if (!name || !reason) return null;
  const id =
    typeof rec.id === "string" && rec.id.trim() ? rec.id.trim() : generateMotifRemovalId();
  return {
    id,
    motifId: typeof rec.motifId === "string" ? rec.motifId.trim() : undefined,
    name,
    description: typeof rec.description === "string" ? rec.description.trim() : undefined,
    reason,
    removedAt:
      typeof rec.removedAt === "string" && rec.removedAt.trim()
        ? rec.removedAt.trim()
        : new Date().toISOString(),
  };
}

export function normalizeSymbolRemoval(raw: unknown): SymbolRemovalRecord | null {
  if (!raw || typeof raw !== "object") return null;
  const rec = raw as Record<string, unknown>;
  const name = typeof rec.name === "string" ? rec.name.trim() : "";
  const reason = typeof rec.reason === "string" ? rec.reason.trim() : "";
  if (!name || !reason) return null;
  const id =
    typeof rec.id === "string" && rec.id.trim() ? rec.id.trim() : generateSymbolRemovalId();
  return {
    id,
    symbolId: typeof rec.symbolId === "string" ? rec.symbolId.trim() : undefined,
    name,
    meaning: typeof rec.meaning === "string" ? rec.meaning.trim() : undefined,
    reason,
    removedAt:
      typeof rec.removedAt === "string" && rec.removedAt.trim()
        ? rec.removedAt.trim()
        : new Date().toISOString(),
  };
}

export function normalizeThemeRemovals(raw: unknown): ThemeRemovalRecord[] {
  if (!Array.isArray(raw)) return [];
  const out: ThemeRemovalRecord[] = [];
  for (const item of raw) {
    const record = normalizeThemeRemoval(item);
    if (record) out.push(record);
  }
  return out.sort((a, b) => b.removedAt.localeCompare(a.removedAt));
}

export function normalizeMotifRemovals(raw: unknown): MotifRemovalRecord[] {
  if (!Array.isArray(raw)) return [];
  const out: MotifRemovalRecord[] = [];
  for (const item of raw) {
    const record = normalizeMotifRemoval(item);
    if (record) out.push(record);
  }
  return out.sort((a, b) => b.removedAt.localeCompare(a.removedAt));
}

export function normalizeSymbolRemovals(raw: unknown): SymbolRemovalRecord[] {
  if (!Array.isArray(raw)) return [];
  const out: SymbolRemovalRecord[] = [];
  for (const item of raw) {
    const record = normalizeSymbolRemoval(item);
    if (record) out.push(record);
  }
  return out.sort((a, b) => b.removedAt.localeCompare(a.removedAt));
}

export function getThemeRemovalsFromMetadata(
  metadata: Record<string, unknown> | undefined | null
): ThemeRemovalRecord[] {
  if (!metadata) return [];
  return normalizeThemeRemovals(metadata.themeRemovals);
}

export function getMotifRemovalsFromMetadata(
  metadata: Record<string, unknown> | undefined | null
): MotifRemovalRecord[] {
  if (!metadata) return [];
  return normalizeMotifRemovals(metadata.motifRemovals);
}

export function getSymbolRemovalsFromMetadata(
  metadata: Record<string, unknown> | undefined | null
): SymbolRemovalRecord[] {
  if (!metadata) return [];
  return normalizeSymbolRemovals(metadata.symbolRemovals);
}

export function isThemeNameRemoved(removals: ThemeRemovalRecord[], name: string): boolean {
  const key = normalizeNameKey(name);
  if (!key) return false;
  return removals.some((record) => normalizeNameKey(record.name) === key);
}

export function isMotifNameRemoved(removals: MotifRemovalRecord[], name: string): boolean {
  const key = normalizeNameKey(name);
  if (!key) return false;
  return removals.some((record) => normalizeNameKey(record.name) === key);
}

export function isSymbolNameRemoved(removals: SymbolRemovalRecord[], name: string): boolean {
  const key = normalizeNameKey(name);
  if (!key) return false;
  return removals.some((record) => normalizeNameKey(record.name) === key);
}

export function createThemeRemovalRecord(
  theme: Pick<BookTheme, "id" | "name" | "type" | "description">,
  reason: string,
  removedAt = new Date().toISOString()
): ThemeRemovalRecord {
  return {
    id: generateThemeRemovalId(),
    themeId: theme.id,
    name: theme.name.trim(),
    type: theme.type,
    description: theme.description.trim() || undefined,
    reason: reason.trim(),
    removedAt,
  };
}

export function createMotifRemovalRecord(
  motif: Pick<BookMotif, "id" | "name" | "description">,
  reason: string,
  removedAt = new Date().toISOString()
): MotifRemovalRecord {
  return {
    id: generateMotifRemovalId(),
    motifId: motif.id,
    name: motif.name.trim(),
    description: motif.description.trim() || undefined,
    reason: reason.trim(),
    removedAt,
  };
}

export function createSymbolRemovalRecord(
  symbol: Pick<BookSymbol, "id" | "name" | "meaning">,
  reason: string,
  removedAt = new Date().toISOString()
): SymbolRemovalRecord {
  return {
    id: generateSymbolRemovalId(),
    symbolId: symbol.id,
    name: symbol.name.trim(),
    meaning: symbol.meaning.trim() || undefined,
    reason: reason.trim(),
    removedAt,
  };
}

export function withThemeRemovalsInMetadata(
  metadata: Record<string, unknown>,
  removals: ThemeRemovalRecord[]
): Record<string, unknown> {
  return {
    ...metadata,
    themeRemovals: normalizeThemeRemovals(removals),
  };
}

export function withMotifRemovalsInMetadata(
  metadata: Record<string, unknown>,
  removals: MotifRemovalRecord[]
): Record<string, unknown> {
  return {
    ...metadata,
    motifRemovals: normalizeMotifRemovals(removals),
  };
}

export function withSymbolRemovalsInMetadata(
  metadata: Record<string, unknown>,
  removals: SymbolRemovalRecord[]
): Record<string, unknown> {
  return {
    ...metadata,
    symbolRemovals: normalizeSymbolRemovals(removals),
  };
}

export function formatThemeRemovalsForExtractPrompt(removals: ThemeRemovalRecord[]): string {
  if (!removals.length) return "";
  return removals
    .slice(0, 40)
    .map((record) => `- "${record.name}": ${record.reason}`)
    .join("\n");
}

export function formatMotifRemovalsForExtractPrompt(removals: MotifRemovalRecord[]): string {
  if (!removals.length) return "";
  return removals
    .slice(0, 40)
    .map((record) => `- "${record.name}": ${record.reason}`)
    .join("\n");
}

export function formatSymbolRemovalsForExtractPrompt(removals: SymbolRemovalRecord[]): string {
  if (!removals.length) return "";
  return removals
    .slice(0, 40)
    .map((record) => `- "${record.name}": ${record.reason}`)
    .join("\n");
}

export function withThemesMotifsInMetadata(
  metadata: Record<string, unknown>,
  themes: BookTheme[],
  motifs: BookMotif[],
  symbols: BookSymbol[]
): Record<string, unknown> {
  return {
    ...metadata,
    themes: sortThemesMajorFirst(themes),
    motifs: sortByName(motifs),
    symbols: sortByName(symbols),
  };
}

/** Seed theme stubs from unique chapter tab name lines when roster is empty. */
export function seedThemesFromChapterTabs(
  chapters: Array<{ id: string; order?: number; tabData?: { themesMotifs?: string } }>,
  removals: ThemeRemovalRecord[] = []
): BookTheme[] {
  const now = new Date().toISOString();
  const byKey = new Map<string, BookTheme>();

  const ordered = [...chapters].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  for (const chapter of ordered) {
    const lines = String(chapter.tabData?.themesMotifs ?? "")
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
    for (const name of lines) {
      if (!isPlausibleThemeName(name)) continue;
      if (isThemeNameRemoved(removals, name)) continue;
      const key = normalizeNameKey(name);
      if (!key) continue;
      const existing = byKey.get(key);
      if (existing) {
        if (!existing.appearances.some((a) => a.chapterId === chapter.id)) {
          existing.appearances.push({ chapterId: chapter.id });
        }
      } else {
        byKey.set(key, {
          id: generateThemeId(),
          name,
          type: "minor",
          description: "",
          evolutionNotes: "",
          firstAppearanceChapterId: chapter.id,
          appearances: [{ chapterId: chapter.id }],
          updatedAt: now,
        });
      }
    }
  }
  return sortThemesMajorFirst(Array.from(byKey.values()));
}

export function themeAppearanceForChapter(
  theme: BookTheme,
  chapterId: string
): ThemeChapterAppearance | undefined {
  return theme.appearances.find((a) => a.chapterId === chapterId);
}

export function motifAppearanceForChapter(
  motif: BookMotif,
  chapterId: string
): MotifChapterAppearance | undefined {
  return motif.appearances.find((a) => a.chapterId === chapterId);
}

export function symbolAppearanceForChapter(
  symbol: BookSymbol,
  chapterId: string
): SymbolChapterAppearance | undefined {
  return symbol.appearances.find((a) => a.chapterId === chapterId);
}

export function themesInChapter(roster: BookTheme[], chapterId: string): BookTheme[] {
  return sortThemesMajorFirst(
    roster.filter((t) => t.appearances.some((a) => a.chapterId === chapterId))
  );
}

export function motifsInChapter(roster: BookMotif[], chapterId: string): BookMotif[] {
  return sortByName(roster.filter((m) => m.appearances.some((a) => a.chapterId === chapterId)));
}

export function symbolsInChapter(roster: BookSymbol[], chapterId: string): BookSymbol[] {
  return sortByName(roster.filter((s) => s.appearances.some((a) => a.chapterId === chapterId)));
}

export function isStructuredThemeRecord(item: unknown): boolean {
  if (!item || typeof item !== "object") return false;
  const rec = item as Record<string, unknown>;
  return (
    typeof rec.name === "string" &&
    (Array.isArray(rec.appearances) ||
      Array.isArray(rec.chapterAppearances) ||
      typeof rec.description === "string" ||
      typeof rec.evolutionNotes === "string" ||
      rec.type === "major" ||
      rec.type === "minor" ||
      typeof rec.id === "string")
  );
}

export function isStructuredMotifRecord(item: unknown): boolean {
  if (!item || typeof item !== "object") return false;
  const rec = item as Record<string, unknown>;
  return (
    typeof rec.name === "string" &&
    (Array.isArray(rec.appearances) ||
      Array.isArray(rec.chapterAppearances) ||
      typeof rec.description === "string" ||
      typeof rec.id === "string")
  );
}

export function isStructuredSymbolRecord(item: unknown): boolean {
  if (!item || typeof item !== "object") return false;
  const rec = item as Record<string, unknown>;
  return (
    typeof rec.name === "string" &&
    (Array.isArray(rec.appearances) ||
      Array.isArray(rec.chapterAppearances) ||
      typeof rec.meaning === "string" ||
      typeof rec.description === "string" ||
      typeof rec.id === "string")
  );
}

/**
 * Prefer full roster JSON from extracted/themes.json.
 * Shape: `{ themes, motifs, symbols }` or legacy name-only arrays.
 */
export function rosterFromExtractedThemesJson(extracted: unknown): {
  themes: BookTheme[];
  motifs: BookMotif[];
  symbols: BookSymbol[];
} {
  if (!extracted || typeof extracted !== "object") {
    return { themes: [], motifs: [], symbols: [] };
  }
  const rec = extracted as Record<string, unknown>;
  const themesRaw = Array.isArray(rec.themes) ? rec.themes : [];
  const motifsRaw = Array.isArray(rec.motifs) ? rec.motifs : [];
  const symbolsRaw = Array.isArray(rec.symbols) ? rec.symbols : [];

  const themesStructured =
    themesRaw.length > 0 && themesRaw.every((item) => isStructuredThemeRecord(item));
  const motifsStructured =
    motifsRaw.length === 0 || motifsRaw.every((item) => isStructuredMotifRecord(item));
  const symbolsStructured =
    symbolsRaw.length === 0 || symbolsRaw.every((item) => isStructuredSymbolRecord(item));

  // Name-only strings in themes array (legacy rollup)
  const themesAreNameOnly =
    themesRaw.length > 0 && themesRaw.every((item) => typeof item === "string");

  if (themesAreNameOnly) {
    return { themes: [], motifs: [], symbols: [] };
  }

  if (!themesStructured && themesRaw.length === 0 && !motifsStructured && !symbolsStructured) {
    return { themes: [], motifs: [], symbols: [] };
  }

  const themes = themesStructured ? normalizeThemeRoster(themesRaw) : [];
  const motifs = motifsStructured ? normalizeMotifRoster(motifsRaw) : [];
  const symbols = symbolsStructured ? normalizeSymbolRoster(symbolsRaw) : [];

  const hasRichData =
    themes.some((t) => t.appearances.length > 0 || t.description || t.evolutionNotes || t.type === "major") ||
    motifs.some((m) => m.appearances.length > 0 || m.description) ||
    symbols.some((s) => s.appearances.length > 0 || s.meaning);

  if (!hasRichData && themes.length === 0 && motifs.length === 0 && symbols.length === 0) {
    return { themes: [], motifs: [], symbols: [] };
  }

  return { themes, motifs, symbols };
}

/** Serialize for extracted/themes.json — full objects, compatible with legacy readers. */
export function themesToExtractedJson(
  themes: BookTheme[],
  motifs: BookMotif[],
  symbols: BookSymbol[]
): {
  themes: Array<{
    id: string;
    name: string;
    type: ThemeType;
    description: string;
    evolutionNotes: string;
    chapterAppearances: Array<{ chapterId: string; manifestation?: string }>;
  }>;
  motifs: Array<{
    id: string;
    name: string;
    description: string;
    chapterAppearances: Array<{ chapterId: string; context?: string }>;
  }>;
  symbols: Array<{
    id: string;
    name: string;
    meaning: string;
    chapterAppearances: Array<{ chapterId: string; context?: string }>;
  }>;
  lastUpdated: string;
} {
  return {
    themes: sortThemesMajorFirst(themes).map((t) => ({
      id: t.id,
      name: t.name,
      type: t.type,
      description: t.description,
      evolutionNotes: t.evolutionNotes,
      chapterAppearances: t.appearances.map((a) => ({
        chapterId: a.chapterId,
        ...(a.manifestation ? { manifestation: a.manifestation } : {}),
      })),
    })),
    motifs: sortByName(motifs).map((m) => ({
      id: m.id,
      name: m.name,
      description: m.description,
      chapterAppearances: m.appearances.map((a) => ({
        chapterId: a.chapterId,
        ...(a.context ? { context: a.context } : {}),
      })),
    })),
    symbols: sortByName(symbols).map((s) => ({
      id: s.id,
      name: s.name,
      meaning: s.meaning,
      chapterAppearances: s.appearances.map((a) => ({
        chapterId: a.chapterId,
        ...(a.context ? { context: a.context } : {}),
      })),
    })),
    lastUpdated: new Date().toISOString(),
  };
}
